import React, { useState, useEffect } from 'react';
import { Popover, Box, ButtonBase, CircularProgress, PopoverProps } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import { getUserGameLists, addGameToList, removeGameFromList, GameListSummary } from '../../services/contents_api';
import { CARD_POPOVER_BG_SX } from '../../constants/cardPopover';

/**
 * 1ゲームを自分のリストへ入れるポップオーバー。カードのステータスメニューから開く。
 *
 * ロジックは詳細ページの `AddToListButton` と同じ API（`getUserGameLists` /
 * `addGameToList`）を使っているが、器はダイアログではなくポップオーバーにしてある。
 * 同じメニューから開くフォルダピッカーと兄弟なので、そちらに形を合わせた方が
 * 「どの階層の操作か」が読める。
 *
 * 【所属状態は開いた時点では分からない】
 * `getUserGameLists` は `item_count` と表紙2枚しか返さず、「このゲームが入っているか」を
 * 持っていない。そのためフォルダピッカーのようなチェックリストには**できない**。
 * 追加専用にして、
 *   - 追加に成功した行は「追加済み」に変える（＋取り消し導線）
 *   - すでに入っていた場合（API が 400 を返す）も赤いエラーにせず「追加済み」に倒す
 * という形にしてある。所属を開いた時点で出したい場合は、一覧APIに真偽値を1つ足す
 * バックエンド側の変更が必要。
 *
 * 【rem は 1rem = 20px】`index.css` が `html { font-size: 20px }` を指定している。
 */

interface GameListPickerPopoverProps {
  open: boolean;
  /** 右クリック時はカーソル位置の仮想アンカー要素を受け取るため、MUIのアンカー型に合わせる */
  anchorEl: PopoverProps['anchorEl'];
  onClose: () => void;
  gameId: number;
}

const GameListPickerPopover: React.FC<GameListPickerPopoverProps> = ({
  open, anchorEl, onClose, gameId,
}) => {
  const navigate = useNavigate();
  const [lists, setLists] = useState<GameListSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** この操作で追加した（＝取り消せる）リスト */
  const [addedIds, setAddedIds] = useState<number[]>([]);
  /** 追加を試したら既に入っていたリスト。追加済み表示にするが取り消しは出さない */
  const [alreadyIds, setAlreadyIds] = useState<number[]>([]);

  const fetchLists = async (nextPage: number, append: boolean) => {
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const res = await getUserGameLists(undefined, nextPage);
      setLists((prev) => (append ? [...prev, ...res.results] : res.results));
      setTotalCount(res.count);
      setPage(nextPage);
      setError(null);
    } catch (e) {
      console.error('リストの取得に失敗しました', e);
      setError('リストの取得に失敗しました');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    // 開き直しのたびに状態を戻す（前回の「追加済み」表示が残ると嘘になる）
    setLists([]);
    setAddedIds([]);
    setAlreadyIds([]);
    setError(null);
    setBusyId(null);
    fetchLists(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, gameId]);

  /**
   * 件数はローカルで加減算する。
   * 元の `AddToListButton` は1件追加するたびに一覧を丸ごと再取得していて、
   * ページを読み進めていても1ページ目に巻き戻っていた。
   */
  const bumpCount = (listId: number, delta: number) => {
    setLists((prev) => prev.map((l) => (
      l.id === listId ? { ...l, item_count: Math.max(0, l.item_count + delta) } : l
    )));
  };

  const handleAdd = async (list: GameListSummary) => {
    setBusyId(list.id);
    setError(null);
    try {
      await addGameToList(list.id, gameId);
      setAddedIds((prev) => [...prev, list.id]);
      bumpCount(list.id, 1);
    } catch (e: any) {
      if (e.response?.status === 400) {
        // 既に入っている。開いた時点では判別できないので、ここで初めて分かる
        setAlreadyIds((prev) => [...prev, list.id]);
      } else {
        console.error('リストへの追加に失敗しました', e);
        setError('リストへの追加に失敗しました');
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleUndo = async (list: GameListSummary) => {
    setBusyId(list.id);
    setError(null);
    try {
      await removeGameFromList(list.id, gameId);
      setAddedIds((prev) => prev.filter((id) => id !== list.id));
      bumpCount(list.id, -1);
    } catch (e) {
      console.error('リストからの削除に失敗しました', e);
      setError('取り消しに失敗しました');
    } finally {
      setBusyId(null);
    }
  };

  const hasMore = lists.length < totalCount;

  const renderThumb = (list: GameListSummary) => {
    const covers = list.preview_items.slice(0, 2);
    if (covers.length === 0) {
      return (
        <Box
          sx={{
            width: 30, height: 30, flexShrink: 0, borderRadius: '4px',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <PlaylistAddIcon sx={{ fontSize: '0.85rem', opacity: 0.35 }} />
        </Box>
      );
    }
    return (
      <Box sx={{ display: 'flex', gap: '2px', width: 30, height: 30, flexShrink: 0 }}>
        {covers.map((item, index) => (
          <Box
            key={item.id}
            component="img"
            src={item.game_cover || '/image/no_image.png'}
            alt=""
            sx={{
              width: covers.length === 1 ? 30 : (index === 0 ? 17 : 11),
              height: '100%',
              objectFit: 'cover',
              borderRadius: '4px',
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
            }}
          />
        ))}
      </Box>
    );
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          // 面は theme.ts の MuiPopover が持っている。背景だけカード用に濃くする。
          sx: {
            width: 'min(288px, calc(100vw - 16px))',
            mt: 0.75,
            p: 0.75,
            ...CARD_POPOVER_BG_SX,
          },
        },
      }}
    >
      <Box sx={{ px: 0.75, pb: 0.75, fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary' }}>
        リストに追加
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : lists.length === 0 ? (
        <Box sx={{ px: 0.75, pb: 0.5 }}>
          <Box sx={{ fontSize: '0.65rem', color: 'rgba(255, 255, 255, 0.45)', lineHeight: 1.5, mb: 1 }}>
            {error ?? 'リストがまだありません。'}
          </Box>
          {!error && (
            <ButtonBase
              onClick={() => { onClose(); navigate('/profile?tab=lists'); }}
              sx={{
                gap: 0.5, px: 0.875, py: 0.625, borderRadius: 1.5,
                color: 'text.secondary',
                backgroundColor: 'rgba(255, 255, 255, 0.04)',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.09)', color: 'text.primary' },
              }}
            >
              <AddIcon sx={{ fontSize: '0.8rem' }} />
              <Box component="span" sx={{ fontSize: '0.65rem', fontWeight: 500, lineHeight: 1.2 }}>
                リストを作成
              </Box>
            </ButtonBase>
          )}
        </Box>
      ) : (
        <>
          {error && (
            <Box sx={{ px: 0.75, pb: 0.5, fontSize: '0.62rem', color: 'error.main', lineHeight: 1.4 }}>
              {error}
            </Box>
          )}

          <Box sx={{ maxHeight: 264, overflowY: 'auto' }}>
            {lists.map((list) => {
              const added = addedIds.includes(list.id);
              const already = alreadyIds.includes(list.id);
              const done = added || already;
              const busy = busyId === list.id;
              return (
                <Box
                  key={list.id}
                  sx={{
                    display: 'flex', alignItems: 'center',
                    borderRadius: '8px',
                    backgroundColor: done ? 'rgba(217, 255, 0, 0.08)' : 'transparent',
                    '&:hover': { backgroundColor: done ? 'rgba(217, 255, 0, 0.12)' : 'action.hover' },
                  }}
                >
                  <ButtonBase
                    onClick={() => { if (!busy && !done) handleAdd(list); }}
                    // 追加済みの行はクリックで何も起きないので、押せる見た目にしない
                    disableRipple={done}
                    sx={{
                      flex: 1, minWidth: 0,
                      justifyContent: 'flex-start', gap: 0.875,
                      px: 0.5, py: 0.5, borderRadius: '8px',
                      cursor: done ? 'default' : 'pointer',
                    }}
                  >
                    {renderThumb(list)}
                    <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                      <Box
                        sx={{
                          fontSize: '0.7rem', fontWeight: done ? 600 : 500, lineHeight: 1.3,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {list.title}
                      </Box>
                      <Box sx={{ fontSize: '0.6rem', color: 'rgba(255, 255, 255, 0.4)', lineHeight: 1.3 }}>
                        {done ? (already ? 'すでに追加済み' : '追加しました') : `${list.item_count}本`}
                      </Box>
                    </Box>
                    {busy ? (
                      <CircularProgress size={14} sx={{ flexShrink: 0, mr: 0.25 }} />
                    ) : done ? (
                      <CheckIcon sx={{ fontSize: '0.85rem', color: 'primary.main', flexShrink: 0, mr: 0.25 }} />
                    ) : null}
                  </ButtonBase>

                  {/* 取り消しはこの操作で追加した行だけ。狭い面での誤タップを戻せるように置く。
                      元から入っていた行（already）に出すと、追加していないものを消すことになる */}
                  {added && !busy && (
                    <ButtonBase
                      onClick={() => handleUndo(list)}
                      sx={{
                        flexShrink: 0, px: 0.625, py: 0.375, mr: 0.25, borderRadius: '6px',
                        fontSize: '0.6rem', fontWeight: 500, lineHeight: 1.2,
                        color: 'rgba(255, 255, 255, 0.45)',
                        '&:hover': { color: 'error.main', backgroundColor: 'rgba(244, 67, 54, 0.12)' },
                      }}
                    >
                      取消
                    </ButtonBase>
                  )}
                </Box>
              );
            })}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pt: 0.75, mt: 0.5, borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
            <ButtonBase
              onClick={() => { onClose(); navigate('/profile?tab=lists'); }}
              sx={{
                gap: 0.375, px: 0.75, py: 0.5, borderRadius: '8px',
                color: 'text.secondary',
                '&:hover': { color: 'text.primary', backgroundColor: 'rgba(255, 255, 255, 0.06)' },
              }}
            >
              <AddIcon sx={{ fontSize: '0.75rem' }} />
              <Box component="span" sx={{ fontSize: '0.62rem', fontWeight: 500, lineHeight: 1.2 }}>
                リストを作成
              </Box>
            </ButtonBase>
            {hasMore && (
              <ButtonBase
                onClick={() => fetchLists(page + 1, true)}
                disabled={loadingMore}
                sx={{
                  ml: 'auto', px: 0.75, py: 0.5, borderRadius: '8px',
                  fontSize: '0.62rem', fontWeight: 500, lineHeight: 1.2,
                  color: 'text.secondary',
                  '&:hover': { color: 'text.primary', backgroundColor: 'rgba(255, 255, 255, 0.06)' },
                }}
              >
                {loadingMore ? <CircularProgress size={12} /> : 'さらに読み込む'}
              </ButtonBase>
            )}
          </Box>
        </>
      )}
    </Popover>
  );
};

export default GameListPickerPopover;
